import { apiClient } from './apiClient';
import { Language } from '../types/language';

export interface CountryInfo {
  code: string;
  name: string;
}

// Кэш для списка стран
let countriesCache: Map<string, CountryInfo[]> = new Map();

/**
 * Получает список всех стран с переводами для указанного языка
 * @param language язык для локализации
 * @returns список стран с кодом и названием
 */
export async function getCountries(language?: Language): Promise<CountryInfo[]> {
  const cacheKey = language || 'default';
  
  // Проверяем кэш
  if (countriesCache.has(cacheKey)) {
    return countriesCache.get(cacheKey)!;
  }

  try {
    const params = language ? { language } : {};
    const response = await apiClient.get<CountryInfo[]>('/countries', { params });
    const countries = response.data;
    
    // Сохраняем в кэш
    countriesCache.set(cacheKey, countries);
    
    return countries;
  } catch (error) {
    console.error('Error fetching countries:', error);
    // Возвращаем пустой список в случае ошибки
    return [];
  }
}

/**
 * Получает список всех стран с переводами на русском языке
 * @returns список стран с кодом и названием на русском
 */
export async function getCountriesRu(): Promise<CountryInfo[]> {
  const cacheKey = 'ru';
  
  // Проверяем кэш
  if (countriesCache.has(cacheKey)) {
    return countriesCache.get(cacheKey)!;
  }

  try {
    const response = await apiClient.get<CountryInfo[]>('/countries/ru');
    const countries = response.data;
    
    // Сохраняем в кэш
    countriesCache.set(cacheKey, countries);
    
    return countries;
  } catch (error) {
    console.error('Error fetching countries (RU):', error);
    // Возвращаем пустой список в случае ошибки
    return [];
  }
}

/**
 * Конвертирует код страны в название
 * @param code код страны (ISO 3166-1 alpha-2)
 * @param language язык для локализации
 * @returns название страны или код, если не найден
 */
export async function codeToName(code: string, language?: Language): Promise<string> {
  if (!code || code.trim() === '') {
    return code;
  }

  try {
    const countries = await getCountries(language);
    const country = countries.find(c => c.code.toUpperCase() === code.toUpperCase());
    return country ? country.name : code;
  } catch (error) {
    console.error('Error converting country code to name:', error);
    return code;
  }
}

/**
 * Конвертирует код страны в название на русском языке
 * @param code код страны (ISO 3166-1 alpha-2)
 * @returns название страны на русском или код, если не найден
 */
export async function codeToNameRu(code: string): Promise<string> {
  if (!code || code.trim() === '') {
    return code;
  }

  try {
    const countries = await getCountriesRu();
    const country = countries.find(c => c.code.toUpperCase() === code.toUpperCase());
    return country ? country.name : code;
  } catch (error) {
    console.error('Error converting country code to name (RU):', error);
    return code;
  }
}

/**
 * Очищает кэш стран (полезно при смене языка)
 */
export function clearCountriesCache(): void {
  countriesCache.clear();
}
